import { useContext, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { ExternalLink } from 'react-feather';
import { BrandInsightsModalContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat/context';
import { useIsFullPageChat } from '@trinity/chat-components/components/Chat/hooks/useIsFullPageChat';
import { ChatContextType, extractBrandInsightsIds, isBrandContext, isBrandInsightsContext } from '@trinity/chat';
import { getLocationFor } from '@trinity/chat-components/utils';
import { To } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { Link } from '../presentation/Link';

export const JumpToPage = () => {

  const jumpToPageState = useJumpToLocation();
  const isFullpageChat = useIsFullPageChat();
  const [modalIsOpen, toggleModal] = useContext(BrandInsightsModalContext);

  const onClick = useCallback(() => {
    if (!isFullpageChat && modalIsOpen) {
      toggleModal();
    }
  }, [isFullpageChat, modalIsOpen, toggleModal]);

  if (!jumpToPageState || !jumpToPageState.to) {
    return null;
  }

  const { to, newTab } = jumpToPageState;

  return (
    <Link to={to} onClick={onClick} target={newTab ? '_blank' : undefined}>
      <TextContainer>
        {isFullpageChat && <Text>Jump to Page </Text>}
        <StyledExternalLink />
      </TextContainer>
    </Link>
  );
};

type JumpToLocationLinkState = {
  to: To;
  newTab?: boolean;
};

const useJumpToLocation = () => {
  const chat = useContext(ActiveChatSessionContext);
  const isFullpageChat = useIsFullPageChat();

  return useMemo<JumpToLocationLinkState | null>(() => {

    switch (chat.context.type) {
      case ChatContextType.KolProfile: {
        return null;
      }

      case ChatContextType.Document: {
        return {
          to: getLocationFor.workspaces.file({
            fileId: chat.context.data.documentId,
          }),
        };
      }

      case ChatContextType.Project: {
        return {
          to: getLocationFor.project.root({
            slug: String(chat.context.data.projectId),
          }),
        };
      }

      case ChatContextType.Brand: {
        return {
          to: getLocationFor.brand.root({
            brandId: chat.context.data.brandId,
          }),
        };
      }

      case ChatContextType.Transcript: {
        // return chat.context.data.callId && chat.context.data.projectId
        //   ? getLocationFor.call.transcript({
        //     callId: chat.context.data.callId,
        //     slug: chat.context.data.projectId.toString(),
        //   })
        //   : getLocationFor.workspaces.transcript({
        //     transcriptId: chat.context.data.transcriptId,
        //   });
        return null;
      }

      case ChatContextType.Folder: {
        return {
          to: getLocationFor.workspaces.folder({
            folderId: chat.context.data.folderId,
          }),
        };
      }

      case ChatContextType.Project: {
        return {
          to: getLocationFor.project.root({
            slug: String(chat.context.data.projectId),
          }),
        };
      }

      case ChatContextType.Global:
        return null;

      case ChatContextType.Agent:
        if (isBrandInsightsContext(chat.context)) {
          const ids = extractBrandInsightsIds(chat.context);

          if (ids.brandId) {
            return {
              to: getLocationFor.brand.root({
                brandId: ids.brandId,
              }),
            };
          } else if (ids.projectId) {
            return {
              to: getLocationFor.project.root({
                slug: String(ids.projectId),
              }),
            };
          } else if (ids.folderId) {
            return {
              to: getLocationFor.workspaces.folder({
                folderId: ids.folderId,
              }),
            };
          } else if (ids.fileId) {
            return {
              to: getLocationFor.workspaces.file({
                fileId: ids.fileId,
              }),
            };
          }
        }
        return { to: chat.context.data.subject?.link ?? null, newTab: true };

      default: {
        throw new UnreachableCaseError(chat.context);
      }

    }
  }, [chat?.context, chat?.id, chat?.identifier, isFullpageChat]);
};

const Text = styled.div`
  color: ${({ theme }) => theme.palette.sentiment.primary.main};
  margin-right: 5px;
`;

const TextContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledExternalLink = styled(ExternalLink)`
  color: ${({ theme }) => theme.palette.sentiment.primary.main};
`;